import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import { BoDialogComponent } from '@3ddv/software-division-components/backoffice/dialog';
import { InfoGridComponent } from '@3ddv/software-division-components/backoffice/info-grid';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import {
  Transaction,
  TransactionError,
  TransactionStatusObj,
} from '../../models/transaction.model';
import { DrawerService } from '../../services/drawer.service';
import { TransactionService } from '../../services/transactions.service';
import { ErrorDetailComponent } from '../error-detail/error-detail.component';
import { DatePipe } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideClipboard, lucideX } from '@ng-icons/lucide';

@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.css'],
  imports: [BoDialogComponent, ButtonComponent, InfoGridComponent, NgIcon],
  viewProviders: [provideIcons({ lucideClipboard, lucideX })],
})
export class ViewComponent implements OnInit, OnDestroy {
  private dialogRef = inject(BrnDialogRef);
  private drawerService = inject(DrawerService);
  private transactionService = inject(TransactionService);
  private toastr = inject(ToastrService);
  private datePipe = new DatePipe('en-US');

  readonly item = input.required<Transaction>();
  readonly transactionStatus = input.required<TransactionStatusObj>();

  private subscriptions: Array<Subscription>;
  public data: Transaction;
  public errors: Array<TransactionError>;
  public isErrorLogOpen = false;

  public get infoItems() {
    return [
      { label: 'Buyer Type', value: this.data.buyerTypeId },
      {
        label: 'Status',
        value:
          this.transactionStatus().items[this.data.status.toUpperCase()].name,
      },
      { label: 'Original Buyer Type', value: this.data.originalBuyerTypeId },
      { label: 'Date', value: this.datePipe.transform(this.data.createdAt) },
      {
        label: 'Balance',
        value: `$ ${this.data.tdcTransaction?.balance ?? ''}`,
      },
      { label: 'Cart ETAG', value: this.data.tdcTransaction?.cartEtag },
      {
        label: 'Credit Available',
        value: `$ ${this.data.tdcTransaction?.creditAvailable ?? ''}`,
      },
      {
        label: 'Insurance Rain Out',
        value: this.data.tdcTransaction?.hasInsuranceRainout,
      },
      {
        label: 'Insurance Rain Out Price',
        value: `$ ${this.data.tdcTransaction?.insuranceRainoutPrice ?? ''}`,
      },
      {
        label: 'Original Balance',
        value: `$ ${this.data.tdcTransaction?.originalBalance ?? ''}`,
      },
      {
        label: 'Original Total Sales Revenue',
        value: `$ ${this.data.tdcTransaction?.originalTotalSalesRevenue ?? ''}`,
      },
      {
        label: 'Total Sales Revenue',
        value: `$ ${this.data.tdcTransaction?.totalSalesRevenue ?? ''}`,
      },
    ];
  }

  constructor() {
    this.subscriptions = [];
  }

  ngOnInit(): void {
    this.data = this.item();

    if (typeof this.data.seats === 'string') {
      const seats = (this.data.seats as string).split(';').map(s => {
        return s.split('S_')[1].split('-');
      });

      this.data.seats = seats as unknown as string[];
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public onDismiss() {
    this.dialogRef.close();
  }

  public onConfirm() {
    this.dialogRef.close();
  }

  openDrawer(payload = {}) {
    this.drawerService.openDrawer(ErrorDetailComponent, payload);
  }

  toggleErrorLog() {
    this.isErrorLogOpen = !this.isErrorLogOpen;

    if (this.isErrorLogOpen) {
      const getErrorsSub = this.transactionService
        .getErrors$(this.data.id)
        .subscribe({
          next: (result): void => {
            this.errors = result.data;
          },
          error: (error: Error): void => {
            console.log('Error finding transaction errors', error);
            this.toastr.error('Error finding transaction errors');
          },
        });

      this.subscriptions.push(getErrorsSub);
    } else {
      this.transactionService.emit({
        id: 'ERROR_LOG_CLOSED',
        transactionId: this.data.id,
      });
    }
  }

  copyToClipboard(value: string) {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = value;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);

    this.toastr.success(null, 'Copied to clipboard', { timeOut: 1000 });
  }
}
